// stages/UsageQuestionsStage.js
import React, { useState } from 'react';
import config from '../config';

const UsageQuestionsStage = ({ 
  userResponses, 
  setUserResponses, 
  analysisData, 
  setAnalysisData,  // NEW: Need this to update analysis with enhanced recommendations
  showContactForm 
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);

  const handleContinue = async () => {
    // Validation - ensure required fields are filled
    if (!userResponses.primaryPurpose || !userResponses.userCount) {
      alert('Please answer the required questions (purpose and user count)');
      return;
    }

    setIsEnhancing(true);

    try {
      // Extract only the essential data needed for enhancement (avoid sending massive objects)
      const essentialAnalysisData = {
        fileName: analysisData.fileName,
        fileType: analysisData.fileType,
        worksheetCount: analysisData.worksheetCount,
        totalRows: analysisData.totalRows,
        totalColumns: analysisData.totalColumns,
        hasMacros: analysisData.hasMacros,
        formulaComplexity: analysisData.formulaComplexity,
        usagePattern: analysisData.usagePattern,
        crossReferences: analysisData.crossReferences,
        // Include the fullAnalysis structure but not the raw data
        fullAnalysis: analysisData.fullAnalysis ? {
          fileName: analysisData.fullAnalysis.fileName,
          fileType: analysisData.fullAnalysis.fileType,
          worksheetCount: analysisData.fullAnalysis.worksheetCount,
          totalRows: analysisData.fullAnalysis.totalRows,
          totalColumns: analysisData.fullAnalysis.totalColumns,
          hasMacros: analysisData.fullAnalysis.hasMacros,
          formulas: analysisData.fullAnalysis.formulas ? { length: analysisData.fullAnalysis.formulas.length } : [],
          crossReferences: analysisData.fullAnalysis.crossReferences,
          formulaTypes: analysisData.fullAnalysis.formulaTypes,
          totalCells: analysisData.fullAnalysis.totalCells,
          populatedCells: analysisData.fullAnalysis.populatedCells,
          usagePattern: analysisData.fullAnalysis.usagePattern,
          formulaComplexity: analysisData.fullAnalysis.formulaComplexity
        } : null
      };

      // Re-run analysis with user responses
      const response = await fetch(`${config.apiBaseUrl}/api/enhance-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalAnalysis: essentialAnalysisData,
          userResponses: userResponses
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to enhance analysis');
      }

      const enhancedData = await response.json();
      
      // Merge the enhanced recommendation back into the original analysis data
      const updatedAnalysisData = {
        ...analysisData,
        recommendation: enhancedData.recommendation,
        usagePattern: enhancedData.usagePattern,
        formulaComplexity: enhancedData.formulaComplexity,
        userResponses: userResponses,
        enhancedAt: enhancedData.enhancedAt
      };
      
      // Update the analysis data with new recommendations
      setAnalysisData(updatedAnalysisData);
      
      // Now proceed to contact form
      showContactForm();
      
    } catch (error) {
      console.error('Enhancement error:', error);
      alert('Error enhancing analysis: ' + error.message);
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="stage">
      <h2 className="stage-title">A Few Questions About How It's Used</h2>
      <div style={{textAlign: 'center'}}>This sharpens the recommendation. The first two are required.</div>

      <div className="questions-section">
        <div className="question">
          <label>What's the spreadsheet's primary function? *</label>
          <select 
            value={userResponses.primaryPurpose || ''}
            onChange={(e) => setUserResponses(prev => ({ ...prev, primaryPurpose: e.target.value }))}
          >
            <option value="">Select...</option>
            <option value="data-storage">Primarily data storage and tracking</option>
            <option value="data-entry">Data entry and collection</option>
            <option value="calculations">Complex calculations and business logic</option>
            <option value="reporting">Reports and dashboards</option>
            <option value="workflow">Workflow and process management</option>
            <option value="hybrid">Mix of the above</option>
          </select>
        </div>
        
        <div className="question">
          <label>How many people use this file regularly? *</label>
          <select 
            value={userResponses.userCount || ''}
            onChange={(e) => setUserResponses(prev => ({ ...prev, userCount: e.target.value }))}
          >
            <option value="">Select...</option>
            <option value="1">Just me</option>
            <option value="2-5">2-5 people</option>
            <option value="6-20">6-20 people</option>
            <option value="20+">More than 20 people</option>
          </select>
        </div>
        
        <div className="question">
          <label>Does the file import or export data from other systems?</label>
          <select 
            value={userResponses.dataIntegration || ''}
            onChange={(e) => setUserResponses(prev => ({ ...prev, dataIntegration: e.target.value }))}
          >
            <option value="">Select...</option>
            <option value="none">No external data</option>
            <option value="manual">Manual copy/paste from other systems</option>
            <option value="files">Import/export files regularly</option>
            <option value="automated">Need automated data connections</option>
          </select>
        </div>
        
        {analysisData.hasMacros && (
          <div className="question">
            <label>We detected macros in your file. How complex are they?</label>
            <select 
              value={userResponses.macroComplexity || ''}
              onChange={(e) => setUserResponses(prev => ({ ...prev, macroComplexity: e.target.value }))}
            >
              <option value="">Select...</option>
              <option value="simple">Simple automation (formatting, basic calculations)</option>
              <option value="moderate">Moderate logic (data processing, validation)</option>
              <option value="complex">Complex business rules and workflows</option>
            </select>
          </div>
        )}
        
        <div className="question">
          <label>What's the most common problem this spreadsheet causes?</label>
          <textarea 
            rows="3"
            placeholder="e.g., multiple people editing causes conflicts, hard to track changes, slow performance..."
            value={userResponses.painPoints || ''}
            onChange={(e) => setUserResponses(prev => ({ ...prev, painPoints: e.target.value }))}
          />
        </div>
      </div>
      
      <div className="text-center">
        <button 
          className="btn-primary"
          onClick={handleContinue}
          disabled={isEnhancing}
        >
          {isEnhancing ? (
            <>
              <span className="spinner">⏳</span> Analyzing...
            </>
          ) : (
            'Next: Get Your Results'
          )}
        </button>
      </div>

      {/* Add some basic styling for the loading state */}
      <style jsx>{`
        .spinner {
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .question label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
      `}</style>
    </div>
  );
};

export default UsageQuestionsStage;